import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../contexts/UserContext";
import { isOrganizationAdmin } from "../../utils/authRoles";
import { getMutationError, getUserMutationToast } from "../../utils/userMutationFeedback";
import { canManageUsersForOrganization } from "../../utils/userCreationPermissions";
import TourModeActionButton from "./TourModeActionButton";
import {
  createOrganizationUser,
  deleteOrganizationUser,
  getOrganizationContextRole,
  getOrganizationUsers,
  updateOrganizationUser,
  updateOrganizationUserRole,
} from "./organizationUsersApi";

const ROLE_OPTIONS = [
  { value: "admin", label: "School Admin" },
  { value: "user", label: "School User" },
];

const formatName = (user) => {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  return fullName || user?.email || "User";
};

const getContextRoleLabel = (contextRole) =>
  contextRole === "admin" ? "School Admin" : "School User";

const INITIAL_INVITE_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  context_role: "user",
};

const INITIAL_EDIT_FORM = {
  first_name: "",
  last_name: "",
  email: "",
};

const OrganizationUsersPage = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const toast = useToast();
  const roleModal = useDisclosure();
  const inviteModal = useDisclosure();
  const removeModal = useDisclosure();
  const editModal = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const organizationId = useMemo(
    () => Number(user?.organization?.id ?? user?.organization_id ?? 0),
    [user]
  );
  const isTour = user?.organization?.tour_mode === true;
  const canManageUsers = isOrganizationAdmin(user) && canManageUsersForOrganization(user, organizationId);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState("user");
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [editErrors, setEditErrors] = useState({});
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [isRemovingUser, setIsRemovingUser] = useState(false);
  const [inviteForm, setInviteForm] = useState(INITIAL_INVITE_FORM);
  const [inviteErrors, setInviteErrors] = useState({});
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  const loadUsers = async () => {
    if (!organizationId) {
      setUsers([]);
      setLoading(false);
      setError(t("organization.users_missing_org", "Could not determine your organization."));
      return;
    }

    try {
      setLoading(true);
      setError("");
      const nextUsers = await getOrganizationUsers(organizationId);
      setUsers(nextUsers);
    } catch (err) {
      setError(t("organization.users_error", "Could not load school users."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canManageUsers) {
      setLoading(false);
      return;
    }

    loadUsers();
  }, [canManageUsers, organizationId]);

  const openRoleModal = (targetUser) => {
    setSelectedUser(targetUser);
    setSelectedRole(getOrganizationContextRole(targetUser));
    roleModal.onOpen();
  };

  const closeRoleModal = () => {
    if (isSavingRole) return;
    setSelectedUser(null);
    setSelectedRole("user");
    roleModal.onClose();
  };

  const resetAndCloseRoleModal = () => {
    setSelectedUser(null);
    setSelectedRole("user");
    roleModal.onClose();
  };

  const openEditModal = (targetUser) => {
    setSelectedUser(targetUser);
    setEditErrors({});
    setEditForm({
      first_name: targetUser?.first_name || "",
      last_name: targetUser?.last_name || "",
      email: targetUser?.email || "",
    });
    editModal.onOpen();
  };

  const closeEditModal = () => {
    if (isSavingUser) return;
    setSelectedUser(null);
    setEditErrors({});
    setEditForm(INITIAL_EDIT_FORM);
    editModal.onClose();
  };

  const resetAndCloseEditModal = () => {
    setSelectedUser(null);
    setEditErrors({});
    setEditForm(INITIAL_EDIT_FORM);
    editModal.onClose();
  };

  const openRemoveModal = (targetUser) => {
    setUserToRemove(targetUser);
    removeModal.onOpen();
  };

  const closeRemoveModal = () => {
    if (isRemovingUser) return;
    setUserToRemove(null);
    removeModal.onClose();
  };

  const resetAndCloseRemoveModal = () => {
    setUserToRemove(null);
    removeModal.onClose();
  };

  const openInviteModal = () => {
    setInviteErrors({});
    setInviteForm(INITIAL_INVITE_FORM);
    inviteModal.onOpen();
  };

  const closeInviteModal = () => {
    if (isCreatingUser) return;
    setInviteErrors({});
    setInviteForm(INITIAL_INVITE_FORM);
    inviteModal.onClose();
  };

  const resetAndCloseInviteModal = () => {
    setInviteErrors({});
    setInviteForm(INITIAL_INVITE_FORM);
    inviteModal.onClose();
  };

  const validateInviteForm = () => {
    const nextErrors = {};

    if (!inviteForm.first_name.trim()) {
      nextErrors.first_name = t("organization.user_first_name_required", "First name is required.");
    }
    if (!inviteForm.last_name.trim()) {
      nextErrors.last_name = t("organization.user_last_name_required", "Last name is required.");
    }
    if (!inviteForm.email.trim()) {
      nextErrors.email = t("organization.user_email_required", "Email is required.");
    }

    setInviteErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const validateEditForm = () => {
    const nextErrors = {};

    if (!editForm.first_name.trim()) {
      nextErrors.first_name = t("organization.user_first_name_required", "First name is required.");
    }
    if (!editForm.last_name.trim()) {
      nextErrors.last_name = t("organization.user_last_name_required", "Last name is required.");
    }
    if (!editForm.email.trim()) {
      nextErrors.email = t("organization.user_email_required", "Email is required.");
    }

    setEditErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRoleSave = async () => {
    if (!selectedUser || !organizationId) return;

    setIsSavingRole(true);
    try {
      await updateOrganizationUserRole(organizationId, selectedUser.id, selectedRole);
      await loadUsers();
      toast(getUserMutationToast("roles"));
      resetAndCloseRoleModal();
    } catch (err) {
      const { message } = getMutationError(err);
      toast({
        title: t("organization.users_role_error_title", "Could not update role"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingRole(false);
    }
  };

  const handleInviteChange = (field) => (event) => {
    const value = event.target.value;
    setInviteForm((current) => ({
      ...current,
      [field]: value,
    }));

    if (inviteErrors[field]) {
      setInviteErrors((current) => ({
        ...current,
        [field]: "",
      }));
    }
  };

  const handleEditChange = (field) => (event) => {
    const value = event.target.value;
    setEditForm((current) => ({
      ...current,
      [field]: value,
    }));

    if (editErrors[field]) {
      setEditErrors((current) => ({
        ...current,
        [field]: "",
      }));
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedUser || isSavingUser) return;
    if (!validateEditForm()) return;

    setIsSavingUser(true);
    try {
      await updateOrganizationUser(selectedUser.id, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
      });
      await loadUsers();
      toast(getUserMutationToast("update"));
      resetAndCloseEditModal();
    } catch (err) {
      const { message } = getMutationError(err);
      toast({
        title: t("organization.users_update_error_title", "Could not update user"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleInviteSubmit = async () => {
    if (!organizationId || isCreatingUser) return;
    if (!validateInviteForm()) return;

    setIsCreatingUser(true);
    try {
      await createOrganizationUser(organizationId, {
        first_name: inviteForm.first_name.trim(),
        last_name: inviteForm.last_name.trim(),
        email: inviteForm.email.trim(),
        context_role: inviteForm.context_role,
      });
      await loadUsers();
      toast(getUserMutationToast("create"));
      resetAndCloseInviteModal();
    } catch (err) {
      const { message } = getMutationError(err);
      toast({
        title: t("organization.users_create_error_title", "Could not invite user"),
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingUser(false);
    }
  };

  const adminUsers = users.filter((targetUser) => getOrganizationContextRole(targetUser) === "admin");
  const adminCount = adminUsers.length;

  const isLastAdmin = (targetUser) =>
    getOrganizationContextRole(targetUser) === "admin" && adminCount <= 1;

  const canRemoveUser = (targetUser) => !isLastAdmin(targetUser);

  const handleRemoveUser = async () => {
    if (!organizationId || !userToRemove || isRemovingUser || !canRemoveUser(userToRemove)) return;

    setIsRemovingUser(true);
    try {
      await deleteOrganizationUser(organizationId, userToRemove.id);
      await loadUsers();
      toast(getUserMutationToast("delete"));
      resetAndCloseRemoveModal();
    } catch (err) {
      const { message, status } = getMutationError(err);
      toast({
        title: t("organization.users_remove_error_title", "Could not remove user"),
        description:
          status === 422
            ? t(
                "organization.users_remove_last_admin",
                "Cannot remove the last school admin"
              )
            : message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRemovingUser(false);
    }
  };

  if (!canManageUsers) {
    return <Navigate to="/app/organization" replace />;
  }

  if (loading) {
    return (
      <Box py={10} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const renderRole = (targetUser) => {
    return getContextRoleLabel(getOrganizationContextRole(targetUser));
  };

  return (
    <VStack align="stretch" spacing={6}>
      <HStack align="start" justify="space-between" spacing={4} flexWrap="wrap">
        <Box>
          <Heading size="lg">{t("organization.users", "Users")}</Heading>
          <Text color="gray.600" mt={2}>
            {t(
              "organization.users_description",
              "Manage the people in your school and keep their access aligned with the role they need."
            )}
          </Text>
        </Box>
        <TourModeActionButton colorScheme="blue" onClick={openInviteModal} isTour={isTour}>
          {t("organization.invite_user", "Invite User")}
        </TourModeActionButton>
      </HStack>

      {users.length === 0 ? (
        <Card>
          <CardBody>
            <Text color="gray.600">
              {t(
                "organization.users_empty",
                "No school users are available yet."
              )}
            </Text>
          </CardBody>
        </Card>
      ) : isMobile ? (
        <VStack align="stretch" spacing={4}>
          {users.map((targetUser) => (
            <Card key={targetUser.id}>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text fontWeight="semibold">{formatName(targetUser)}</Text>
                    <Text color="gray.600" fontSize="sm">
                      {targetUser.email}
                    </Text>
                  </Box>
                  <Badge alignSelf="start" colorScheme="blue" variant="subtle">
                    {renderRole(targetUser)}
                  </Badge>
                  <TourModeActionButton
                    alignSelf="start"
                    variant="outline"
                    onClick={() => openEditModal(targetUser)}
                    isTour={isTour}
                  >
                    {t("organization.edit_user", "Edit")}
                  </TourModeActionButton>
                  <TourModeActionButton
                    alignSelf="start"
                    variant="outline"
                    onClick={() => openRoleModal(targetUser)}
                    isTour={isTour}
                  >
                    {t("organization.change_role", "Change role")}
                  </TourModeActionButton>
                  <TourModeActionButton
                    alignSelf="start"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => openRemoveModal(targetUser)}
                    isTour={isTour}
                    isDisabled={!canRemoveUser(targetUser)}
                  >
                    {t("organization.remove_user", "Remove")}
                  </TourModeActionButton>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      ) : (
        <Card>
          <CardBody>
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>{t("organization.user_name", "Name")}</Th>
                    <Th>{t("organization.user_email", "Email")}</Th>
                    <Th>{t("organization.user_role", "Role")}</Th>
                    <Th>{t("organization.user_actions", "Actions")}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((targetUser) => (
                    <Tr key={targetUser.id}>
                      <Td>{formatName(targetUser)}</Td>
                      <Td>{targetUser.email}</Td>
                      <Td>
                        <Badge colorScheme="blue" variant="subtle">
                          {renderRole(targetUser)}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <TourModeActionButton
                            size="sm"
                            variant="outline"
                            onClick={() => openEditModal(targetUser)}
                            isTour={isTour}
                          >
                            {t("organization.edit_user", "Edit")}
                          </TourModeActionButton>
                          <TourModeActionButton
                            size="sm"
                            variant="outline"
                            onClick={() => openRoleModal(targetUser)}
                            isTour={isTour}
                          >
                            {t("organization.change_role", "Change role")}
                          </TourModeActionButton>
                          <TourModeActionButton
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => openRemoveModal(targetUser)}
                            isTour={isTour}
                            isDisabled={!canRemoveUser(targetUser)}
                          >
                            {t("organization.remove_user", "Remove")}
                          </TourModeActionButton>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}

      <Modal isOpen={roleModal.isOpen} onClose={closeRoleModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("organization.change_role", "Change role")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold">{formatName(selectedUser)}</Text>
                <Text color="gray.600" fontSize="sm">
                  {selectedUser?.email}
                </Text>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={2}>
                  {t("organization.user_role", "Role")}
                </Text>
                <Select
                  value={selectedRole}
                  onChange={(event) => setSelectedRole(event.target.value)}
                >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {t(
                        option.value === "admin"
                          ? "organization.role_admin"
                          : "organization.role_user",
                        option.label
                      )}
                    </option>
                  ))}
                </Select>
              </Box>

              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  {t(
                    "organization.users_role_confirm",
                    "Saving will update this user's school access immediately."
                  )}
                </AlertDescription>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={closeRoleModal} isDisabled={isSavingRole}>
                {t("common.cancel", "Cancel")}
              </Button>
              <TourModeActionButton colorScheme="blue" onClick={handleRoleSave} isTour={isTour} isLoading={isSavingRole}>
                {t("organization.confirm_role_change", "Confirm role change")}
              </TourModeActionButton>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={editModal.isOpen} onClose={closeEditModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("organization.edit_user", "Edit user")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold">{formatName(selectedUser)}</Text>
                <Text color="gray.600" fontSize="sm">
                  {selectedUser?.email}
                </Text>
              </Box>

              <FormControl isRequired isInvalid={Boolean(editErrors.first_name)}>
                <FormLabel>{t("organization.user_first_name", "First Name")}</FormLabel>
                <Input
                  value={editForm.first_name}
                  onChange={handleEditChange("first_name")}
                />
                <FormErrorMessage>{editErrors.first_name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={Boolean(editErrors.last_name)}>
                <FormLabel>{t("organization.user_last_name", "Last Name")}</FormLabel>
                <Input
                  value={editForm.last_name}
                  onChange={handleEditChange("last_name")}
                />
                <FormErrorMessage>{editErrors.last_name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={Boolean(editErrors.email)}>
                <FormLabel>{t("organization.user_email", "Email")}</FormLabel>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={handleEditChange("email")}
                />
                <FormErrorMessage>{editErrors.email}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={closeEditModal} isDisabled={isSavingUser}>
                {t("common.cancel", "Cancel")}
              </Button>
              <TourModeActionButton colorScheme="blue" onClick={handleEditSubmit} isTour={isTour} isLoading={isSavingUser}>
                {t("organization.save_user", "Save user")}
              </TourModeActionButton>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={inviteModal.isOpen} onClose={closeInviteModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("organization.invite_user", "Invite User")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <FormControl isRequired isInvalid={Boolean(inviteErrors.first_name)}>
                <FormLabel>{t("organization.user_first_name", "First Name")}</FormLabel>
                <Input
                  value={inviteForm.first_name}
                  onChange={handleInviteChange("first_name")}
                />
                <FormErrorMessage>{inviteErrors.first_name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={Boolean(inviteErrors.last_name)}>
                <FormLabel>{t("organization.user_last_name", "Last Name")}</FormLabel>
                <Input
                  value={inviteForm.last_name}
                  onChange={handleInviteChange("last_name")}
                />
                <FormErrorMessage>{inviteErrors.last_name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={Boolean(inviteErrors.email)}>
                <FormLabel>{t("organization.user_email", "Email")}</FormLabel>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={handleInviteChange("email")}
                />
                <FormErrorMessage>{inviteErrors.email}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>{t("organization.user_role", "Role")}</FormLabel>
                <Select
                  value={inviteForm.context_role}
                  onChange={handleInviteChange("context_role")}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {t(
                        option.value === "admin"
                          ? "organization.role_admin"
                          : "organization.role_user",
                        option.label
                      )}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={closeInviteModal} isDisabled={isCreatingUser}>
                {t("common.cancel", "Cancel")}
              </Button>
              <TourModeActionButton
                colorScheme="blue"
                onClick={handleInviteSubmit}
                isTour={isTour}
                isLoading={isCreatingUser}
              >
                {t("organization.send_invite", "Send Invite")}
              </TourModeActionButton>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={removeModal.isOpen} onClose={closeRemoveModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("organization.remove_user_title", "Remove user from school")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold">{formatName(userToRemove)}</Text>
                <Text color="gray.600" fontSize="sm">
                  {userToRemove?.email}
                </Text>
              </Box>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  {t(
                    "organization.remove_user_warning",
                    "This will remove the user from this school. This action cannot be undone."
                  )}
                </AlertDescription>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={closeRemoveModal} isDisabled={isRemovingUser}>
                {t("common.cancel", "Cancel")}
              </Button>
              <TourModeActionButton
                colorScheme="red"
                onClick={handleRemoveUser}
                isTour={isTour}
                isLoading={isRemovingUser}
                isDisabled={!canRemoveUser(userToRemove)}
              >
                {t("organization.confirm_remove_user", "Confirm Remove")}
              </TourModeActionButton>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default OrganizationUsersPage;
